import { use } from "react";

import { InputField as BaseInputField } from "@bokio/design-system-base/components/InputField/InputField";
import { Badge } from "@bokio/designsystem/components/Badge/Badge";
import Icon from "@bokio/designsystem/components/Icon/Icon";
import { SG } from "@bokio/designsystem/components/SpacingGroup/SpacingGroup";
import { InfoTooltip } from "@bokio/designsystem/components/Tooltip";
import { Tooltip } from "@bokio/designsystem/components/Tooltip/Tooltip";

import { FormContext } from "./Form";
import { Validation } from "./Validation/Validation.internals";

import type { LabelBadge, LabelTooltip } from "./LabelFor/LabelFor";
import type { RuleValidationResult } from "@bokio/shared/validation/entityValidator";
import type { FC, FormEvent, InputHTMLAttributes, KeyboardEvent, ReactNode, RefObject } from "react";

import * as labelForStyles from "./LabelFor/labelFor.scss";

type InputElementAttributes = InputHTMLAttributes<HTMLInputElement>;

export type InputFieldPropsBase = {
	/**
	 * Formats the input field value on blur.
	 */
	formatter?: (value: string) => string;
	/**
	 * Prepend text inside the input field. Useful for adding e.g. currency symbol.
	 */
	prefix?: string;
	/**
	 * Append text inside the input field. Useful for adding e.g. counting unit.
	 * @note Mutually exclusive with `inlineControls`.
	 */
	postfix?: string;
	placeholder?: string;
	/**
	 * Append a tooltip icon next to input field.
	 */
	tooltip?: LabelTooltip;

	/**
	 * Validation errors from validators using `FieldRuleFactory`.
	 */
	errors?: { errors: RuleValidationResult[] };
	/**
	 * Render a node that could be absolutely positioned in relation to the actual <input>.
	 * @note Mutually exclusive with `postfix`.
	 */
	inlineControls?: (props: { disabled: boolean | undefined; value: string | undefined }) => ReactNode;

	label?: ReactNode;
	labelTooltip?: LabelTooltip;
	hint?: string;

	onChange?: (val: string) => void; // EE 2025-08-06: Should be (val: string | undefined) => void to match with the value type
	onFocus?: (e: FormEvent<HTMLInputElement>) => void;
	onBlur?: (e: FormEvent<HTMLInputElement>) => void;
	onClick?: (e: FormEvent<HTMLInputElement>) => void;

	testId?: string;
	labelTestId?: string;

	/**
	 * Set class name to the actual input element.
	 */
	className?: string;
	/**
	 * Set class name to the {@link LabelFor} component that is wrapping the actual input element.
	 */
	labelClassName?: string;
	/**
	 * Set class name to the outermost {@link Field} component that is wrapping {@link LabelFor}.
	 */
	wrapperClassName?: string;

	/**
	 * Attach ref to the actual input element.
	 */
	innerRef?: RefObject<HTMLInputElement | null>;
	value: string | undefined;
	disabled?: boolean;
	/**
	 * Marks the field as required by:
	 * - Adding an asterisk (*) next to the label
	 * - Setting the `aria-required` attribute on the input element for accessibility
	 *
	 * Note: This does not add validation. To validate that the field is required,
	 * use `FieldValidator` with `FieldRuleFactory.Required()`.
	 */
	markAsRequired?: boolean;
	readOnly?: boolean;
	maxLength?: number;
	autoFocus?: boolean;
	// JR 2018-10-24:
	// some password managers aggressively autofill input fields based on the name attribute,
	// since we don't post with forms we should only pass a name attr when we want to allow autofill
	nameForAutofill?: string;
	inputMode?: "none" | "text" | "tel" | "url" | "email" | "numeric" | "decimal" | "search";
	// SS 2020-11-24:
	// This list is not exhaustive, please refer to MDN and add new ones yourself if anything is missing https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
	autoComplete?:
		| "off"
		| "name"
		| "given-name"
		| "family-name"
		| "email"
		| "username"
		| "new-password"
		| "current-password"
		| "one-time-code"
		| "organization"
		| "address-line1"
		| "address-line2"
		| "country-name"
		| "postal-code"
		| "tel"
		| "url";
	/**
	 * Additional props to the actual input element.
	 */
	fieldProps?: InputElementAttributes;
	badge?: LabelBadge | LabelBadge[];
	onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
};

export type InputFieldProps = InputFieldPropsBase;
/** @deprecated Use the typed version instead
 * We should migrate away all usages from this type.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type UnTypedInputFieldProps = TypedInputFieldProps<any>;
export type TypedInputFieldProps<T> = Omit<InputFieldPropsBase, "value" | "onChange"> & {
	value: T | undefined;
	onChange?: (value: T | undefined) => void;
};

export const InputField: FC<InputFieldProps> = ({ disabled, tooltip, labelTooltip, badge, errors, ...baseProps }) => {
	const { disableAllFields } = use(FormContext);

	const tooltipNode: ReactNode = tooltip ? (
		<Tooltip contentGenerator={() => tooltip.message} placement={tooltip.placement} trackinfo={tooltip.trackinfo}>
			<Icon className={labelForStyles.tooltipIcon} name={tooltip.iconName || "info"} size="24" color="grey" />
		</Tooltip>
	) : undefined;

	const renderLabelExtra = labelTooltip
		? () => <InfoTooltip placement={labelTooltip.placement} contentGenerator={() => labelTooltip.message} />
		: undefined;

	const renderFooter = (errs: readonly unknown[] | undefined, testId: string | undefined) => {
		const typedErrors = errs as RuleValidationResult[] | undefined;
		if (badge) {
			return (
				<SG padding={[4, 0, 0]}>
					<SG horizontal wrap>
						{!Array.isArray(badge) && <Badge color="ghost" name={badge.name} onClick={badge.onClick} />}
						{Array.isArray(badge) &&
							badge.map(({ name, onClick }, index) => (
								// eslint-disable-next-line @eslint-react/no-array-index-key
								<Badge key={index} color="ghost" name={name} onClick={onClick} />
							))}
					</SG>
					<Validation testId={`Validation_Inline_${testId}`} errors={typedErrors} />
				</SG>
			);
		}
		return <Validation testId={`Validation_Inline_${testId}`} errors={typedErrors} />;
	};

	return (
		<BaseInputField
			{...baseProps}
			disabled={disabled}
			disabledOverride={disableAllFields}
			errors={errors}
			tooltip={tooltipNode}
			renderLabelExtra={renderLabelExtra}
			renderFooter={renderFooter}
		/>
	);
};
